import { useCallback, useEffect, useState } from 'react'
import { UserPlus, Shield, Link2, RefreshCw, KeyRound } from 'lucide-react'
import apiClient from '@/utils/api'
import { useToast } from '@/components/Toast'
import { getSessionInfo } from '@/utils/auth'
import { apiErrorMessage, parseApiError } from '@/utils/apiErrors'
import { getSelectedAwsLinkId, setSelectedAwsLinkId } from '@/utils/beaconAwsLink'

type Member = {
  id: string
  user_id: string
  email?: string | null
  role: string
  status: string
}

type Invite = {
  id: string
  email: string
  role: string
  token: string
  status: string
  expires_at?: string | null
  email_status?: string | null
  email_detail?: string | null
  email_sent_at?: string | null
}

type LastInviteResult = {
  accept_url: string
  email_status: string
  email_detail?: string | null
  email: string
}

type AwsAccount = {
  id: string
  aws_account_id: string
  label?: string | null
  role_arn?: string | null
  is_default: boolean
  status: string
  region?: string | null
}

type TenantInfo = {
  id: string
  name: string
  aws_link_status: string
  primary_aws_account_id?: string | null
  marketplace_customer_id?: string | null
  subscription_tier?: string | null
  membership_role?: string
  seats?: { active: number; pending_invites: number; used: number; max: number }
  aws_accounts?: { max: number; linked: number; accounts: AwsAccount[] }
  sso?: { enabled_configs: number }
  permissions?: { can_write: boolean; can_approve: boolean; can_admin: boolean }
}

export default function BeaconAdmin() {
  const { toast } = useToast()
  const session = getSessionInfo()
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'admin'>('member')
  const [acceptToken, setAcceptToken] = useState('')
  const [lastInvite, setLastInvite] = useState<LastInviteResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [awsId, setAwsId] = useState('')
  const [awsLabel, setAwsLabel] = useState('')
  const [awsRoleArn, setAwsRoleArn] = useState('')
  const [awsExternalId, setAwsExternalId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [t, m] = await Promise.all([
        apiClient.get('/api/v1/beacon/admin/tenant'),
        apiClient.get('/api/v1/beacon/admin/members'),
      ])
      setTenant(t.data)
      setMembers(m.data.members || [])
      setInvites(m.data.invites || [])
    } catch (err: any) {
      toast.error('Failed to load tenant admin', apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  // Deep link from invite email: /beacon/admin?invite=<token>
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('invite')
      if (token) setAcceptToken(token)
    } catch {
      /* ignore */
    }
  }, [])

  const canAdmin =
    tenant?.membership_role === 'owner' ||
    tenant?.membership_role === 'admin' ||
    session.isAdmin

  const invite = async () => {
    if (!email.trim()) return
    setBusy(true)
    try {
      const { data } = await apiClient.post('/api/v1/beacon/admin/invites', {
        email: email.trim(),
        role,
      })
      const status = data?.email?.status || data?.invite?.email_status || 'unknown'
      const acceptUrl = data?.accept_url || ''
      setLastInvite({
        accept_url: acceptUrl,
        email_status: status,
        email_detail: data?.email?.detail || data?.invite?.email_detail,
        email: data?.invite?.email || email.trim(),
      })
      toast.success(
        'Invite created',
        status === 'sent'
          ? `Email sent to ${data?.invite?.email || email}`
          : `Email ${status} — copy the accept link below`
      )
      setEmail('')
      if (data?.invite?.token) {
        setAcceptToken(data.invite.token)
      }
      await load()
    } catch (err: any) {
      const parsed = parseApiError(err)
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      let msg = parsed.message || apiErrorMessage(err)
      if (status === 402 && detail && typeof detail === 'object') {
        const d = detail as { message?: string; upgrade_url?: string }
        msg = d.message || msg
        if (d.upgrade_url) msg = `${msg} Upgrade via AWS Marketplace.`
      }
      toast.error(status === 402 ? 'Seat limit reached' : 'Invite failed', msg)
    } finally {
      setBusy(false)
    }
  }

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('Copied', label)
    } catch {
      toast.error('Copy failed', 'Select and copy manually')
    }
  }

  const resendInvite = async (inviteId: string) => {
    setBusy(true)
    try {
      const { data } = await apiClient.post(`/api/v1/beacon/admin/invites/${inviteId}/resend`)
      const status = data?.email?.status || 'unknown'
      const acceptUrl = data?.accept_url || ''
      if (acceptUrl) {
        setLastInvite({
          accept_url: acceptUrl,
          email_status: status,
          email_detail: data?.email?.detail,
          email: data?.invite?.email || '',
        })
      }
      toast.success('Invite resent', `Email status: ${status}`)
      await load()
    } catch (err: any) {
      toast.error('Resend failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const acceptInvite = async () => {
    if (!acceptToken.trim()) return
    setBusy(true)
    try {
      await apiClient.post('/api/v1/beacon/admin/invites/accept', {
        token: acceptToken.trim(),
      })
      toast.success('Invite accepted', 'You now share this tenant workspace')
      setAcceptToken('')
      await load()
    } catch (err: any) {
      toast.error('Accept failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (membershipId: string) => {
    setBusy(true)
    try {
      await apiClient.post(`/api/v1/beacon/admin/members/${membershipId}/revoke`)
      toast.success('Membership revoked')
      await load()
    } catch (err: any) {
      toast.error('Revoke failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const changeRole = async (membershipId: string, next: 'admin' | 'member') => {
    setBusy(true)
    try {
      await apiClient.patch(`/api/v1/beacon/admin/members/${membershipId}/role`, {
        role: next,
      })
      toast.success('Role updated', `Now ${next}`)
      await load()
    } catch (err: any) {
      toast.error('Role change failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const linkAws = async () => {
    if (!awsId.trim()) return
    setBusy(true)
    try {
      await apiClient.post('/api/v1/beacon/admin/aws-accounts', {
        aws_account_id: awsId.trim(),
        label: awsLabel.trim() || undefined,
        role_arn: awsRoleArn.trim() || undefined,
        external_id: awsExternalId.trim() || undefined,
        make_default: !(tenant?.aws_accounts?.linked),
      })
      toast.success('AWS account linked')
      setAwsId('')
      setAwsLabel('')
      setAwsRoleArn('')
      setAwsExternalId('')
      await load()
    } catch (err: any) {
      toast.error('Link failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const setDefaultAws = async (linkId: string) => {
    setBusy(true)
    try {
      await apiClient.post(`/api/v1/beacon/admin/aws-accounts/${linkId}/default`)
      toast.success('Default AWS account updated')
      await load()
    } catch (err: any) {
      toast.error('Default failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const unlinkAws = async (linkId: string) => {
    setBusy(true)
    try {
      await apiClient.delete(`/api/v1/beacon/admin/aws-accounts/${linkId}`)
      toast.success('AWS account unlinked')
      await load()
    } catch (err: any) {
      toast.error('Unlink failed', apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading company workspace…</p>
  }

  const seats = tenant?.seats
  const awsAccounts = (tenant?.aws_accounts?.accounts || []).filter((a) => a.status === 'linked')

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-beacon-navy tracking-tight">Company workspace</h2>
        <p className="mt-1 text-sm text-gray-600">
          Members share one Beacon ledger. Link multiple AWS accounts, enforce seat limits by Marketplace tier,
          and keep approvals to admins.
        </p>
      </div>

      {/* Seats + permissions */}
      <section className="border-b border-gray-200 pb-6">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">Tier</dt>
            <dd className="font-medium text-gray-900 capitalize">{tenant?.subscription_tier || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Seats</dt>
            <dd className="font-medium text-gray-900">
              {seats ? `${seats.used} / ${seats.max}` : '—'}
              {seats ? (
                <span className="block text-xs text-gray-500 font-normal">
                  {seats.active} active · {seats.pending_invites} pending invites
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Your permissions</dt>
            <dd className="font-medium text-gray-900 text-xs space-y-0.5">
              <div>write: {tenant?.permissions?.can_write ? 'yes' : 'no'}</div>
              <div>approve: {tenant?.permissions?.can_approve ? 'yes' : 'no'}</div>
            </dd>
          </div>
        </dl>
      </section>

      {/* AWS accounts */}
      <section className="border-b border-gray-200 pb-6">
        <div className="flex items-center gap-2 text-beacon-navy font-medium">
          <Link2 className="h-4 w-4" />
          <span>AWS accounts</span>
          <span className="text-xs font-normal text-gray-500">
            {tenant?.aws_accounts?.linked ?? 0} / {tenant?.aws_accounts?.max ?? '—'} linked
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Inventory and write actions use the default account (or{' '}
          <code className="font-mono">X-Beacon-Aws-Link</code> / <code className="font-mono">aws_link_id</code>).
          Optional cross-account <code className="font-mono">role_arn</code> + <code className="font-mono">external_id</code>.
        </p>
        <ul className="mt-3 divide-y divide-gray-100">
          {awsAccounts.map((a) => (
            <li key={a.id} className="py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm font-mono text-gray-900">
                  {a.aws_account_id}
                  {a.is_default ? (
                    <span className="ml-2 text-xs font-sans text-beacon-orange">default</span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-500">
                  {a.label || '—'}
                  {a.role_arn ? ` · ${a.role_arn}` : ' · env credentials'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setSelectedAwsLinkId(a.id)
                    toast.success('Chat AWS account', `Chat will use ${a.aws_account_id}`)
                  }}
                  className="text-xs px-2 py-1 rounded border border-teal-300 text-teal-800 hover:bg-teal-50"
                >
                  {getSelectedAwsLinkId() === a.id ? 'Selected for Chat' : 'Use for Chat'}
                </button>
                {canAdmin && !a.is_default && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setDefaultAws(a.id)}
                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Make default
                  </button>
                )}
                {canAdmin && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => unlinkAws(a.id)}
                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                  >
                    Unlink
                  </button>
                )}
              </div>
            </li>
          ))}
          {awsAccounts.length === 0 && (
            <li className="py-3 text-sm text-gray-500">No AWS accounts linked yet.</li>
          )}
        </ul>
        {canAdmin && (
          <div className="mt-4 space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={awsId}
                onChange={(e) => setAwsId(e.target.value)}
                placeholder="12-digit AWS account id"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
              <input
                value={awsLabel}
                onChange={(e) => setAwsLabel(e.target.value)}
                placeholder="Label (optional)"
                className="sm:w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={awsRoleArn}
                onChange={(e) => setAwsRoleArn(e.target.value)}
                placeholder="role_arn (optional)"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
              <input
                value={awsExternalId}
                onChange={(e) => setAwsExternalId(e.target.value)}
                placeholder="external_id"
                className="sm:w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              />
              <button
                type="button"
                disabled={busy}
                onClick={linkAws}
                className="rounded-lg bg-beacon-orange text-beacon-navy px-4 py-2 text-sm font-semibold disabled:opacity-50"
              >
                Link
              </button>
            </div>
          </div>
        )}
      </section>

      {/* SSO */}
      <section className="border-b border-gray-200 pb-6">
        <div className="flex items-center gap-2 text-beacon-navy font-medium">
          <KeyRound className="h-4 w-4" />
          <span>SSO (OIDC)</span>
        </div>
        <p className="mt-2 text-sm text-gray-700">
          Enabled configs for this tenant:{' '}
          <span className="font-medium">{tenant?.sso?.enabled_configs ?? 0}</span>
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Register IdP via <code className="font-mono">POST /api/v1/sso/config</code>. See{' '}
          <code className="font-mono">docs/beacon-sso.md</code>. Email/password and Beacon dev tokens keep working
          locally. SAML: use an IdP OIDC bridge.
        </p>
      </section>

      {/* Invite */}
      {canAdmin && (
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2 text-beacon-navy font-medium">
            <UserPlus className="h-4 w-4" />
            <span>Invite teammate</span>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {/* DB keeps member; UI aligns with Users → Team Member */}
              <option value="member">Team Member (write, not approve)</option>
              <option value="admin">Admin (write + approve)</option>
            </select>
            <button
              type="button"
              disabled={busy}
              onClick={invite}
              className="rounded-lg bg-beacon-orange text-beacon-navy px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              Invite
            </button>
          </div>
        </section>
      )}

      {lastInvite && (
        <section className="border-b border-gray-200 pb-6">
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Latest invite</h3>
          <p className="mt-2 text-sm text-gray-700">
            {lastInvite.email} · email{' '}
            <span className="font-semibold text-beacon-navy">{lastInvite.email_status}</span>
            {lastInvite.email_detail ? (
              <span className="text-xs text-gray-500"> — {lastInvite.email_detail}</span>
            ) : null}
          </p>
          {(lastInvite.email_status === 'stubbed' ||
            lastInvite.email_status === 'sandbox_unverified' ||
            lastInvite.email_status === 'failed') && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
              Live invite email is not available yet (SES From not verified or still sandboxed).
              Share the accept link below with your teammate — it works without the email.
            </p>
          )}
          <div className="mt-3 flex flex-col gap-2">
            <label className="text-xs text-gray-500">Accept link (always copyable)</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={lastInvite.accept_url}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-mono bg-gray-50"
              />
              <button
                type="button"
                onClick={() => copyText(lastInvite.accept_url, 'Accept link')}
                className="rounded-lg border border-beacon-orange bg-beacon-orange/15 px-3 py-2 text-sm font-semibold text-beacon-navy hover:bg-beacon-orange/25"
              >
                Copy accept link
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Accept invite */}
      <section className="border-b border-gray-200 pb-6">
        <div className="flex items-center gap-2 text-beacon-navy font-medium">
          <Shield className="h-4 w-4" />
          <span>Accept invite</span>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={acceptToken}
            onChange={(e) => setAcceptToken(e.target.value)}
            placeholder="Paste invite token"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
          />
          <button
            type="button"
            disabled={busy}
            onClick={acceptInvite}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Accept
          </button>
        </div>
      </section>

      {/* Members */}
      <section>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Members</h3>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        <ul className="mt-3 divide-y divide-gray-100">
          {members.map((m) => (
            <li key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{m.email || m.user_id}</p>
                <p className="text-xs text-gray-500">
                  {m.role === 'member' ? 'Team Member' : m.role === 'admin' ? 'Admin' : m.role === 'owner' ? 'Owner' : m.role}
                  {' · '}{m.status}
                  {m.role === 'member' ? ' · can write, cannot approve' : null}
                  {m.role === 'admin' || m.role === 'owner' ? ' · can write + approve' : null}
                </p>
              </div>
              {canAdmin && m.role !== 'owner' && m.status === 'active' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => changeRole(m.id, m.role === 'admin' ? 'member' : 'admin')}
                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Make {m.role === 'admin' ? 'Team Member' : 'Admin'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => revoke(m.id)}
                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                  >
                    Revoke
                  </button>
                </div>
              )}
            </li>
          ))}
          {members.length === 0 && (
            <li className="py-4 text-sm text-gray-500">No members yet.</li>
          )}
        </ul>

        {invites.filter((i) => i.status === 'pending').length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Pending invites</h3>
            <ul className="mt-2 space-y-3">
              {invites
                .filter((i) => i.status === 'pending')
                .map((i) => {
                  const link = `${window.location.origin}/beacon/admin?invite=${encodeURIComponent(i.token)}`
                  return (
                    <li key={i.id} className="rounded-lg border border-gray-100 p-3 text-xs text-gray-700">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm text-gray-900">
                            {i.email}{' '}
                            <span className="font-normal text-gray-500">
                              ({i.role === 'member' ? 'Team Member' : i.role === 'admin' ? 'Admin' : i.role})
                            </span>
                          </p>
                          <p className="mt-0.5 text-gray-500">
                            email:{' '}
                            <span className="font-semibold text-beacon-navy">
                              {i.email_status || 'not_sent'}
                            </span>
                            {i.expires_at ? ` · expires ${i.expires_at}` : null}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => copyText(link, 'Accept link')}
                            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                          >
                            Copy link
                          </button>
                          {canAdmin && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => resendInvite(i.id)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Resend email
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="mt-2 font-mono break-all text-gray-500">{link}</p>
                    </li>
                  )
                })}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}
